import { Container, Graphics } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE } from '../config/tokens';
import type { Layout } from '../render/stage';
import { button, iconButton, label, spriteFit } from './components';
import { openModal } from './modal';
import type { ProductDef } from '../monetization/products';

/**
 * TEST MODE stand-ins for the App Store purchase sheet and a rewarded
 * video. Clearly labelled, so nobody mistakes them for real payments.
 */
export function testPurchaseSheet(parent: Container, l: Layout | undefined, p: ProductDef, price: string): Promise<boolean> {
  return new Promise((resolve) => {
    const w = 720;
    const m = openModal(parent, l, w, 640);
    const tag = new Container();
    const tt = label('TEST MODE', 26, { weight: FONT.weight.heading, color: 'cream' });
    tag.addChild(new Graphics().roundRect(-tt.width / 2 - 20, -22, tt.width + 40, 44, 22).fill(COLORS.wood), tt);
    tag.position.set(w / 2, 60);
    const title = label(p.title, FONT.size.screenTitle, { weight: FONT.weight.heading });
    title.position.set(w / 2, 140);
    const pr = label(price, FONT.size.celebration, { weight: FONT.weight.heading, color: 'raspberry' });
    pr.position.set(w / 2, 225);
    const note = label('No money is charged.\nReal purchases work in the store app.', FONT.size.secondary, { weight: FONT.weight.body });
    note.style.align = 'center';
    note.position.set(w / 2, 320);
    const done = (ok: boolean) => {
      m.close();
      resolve(ok);
    };
    const buy = button('Buy (test)', 480, 'primary', () => done(true), 92);
    buy.position.set(w / 2, 450);
    const cancel = button('Cancel', 480, 'secondary', () => done(false), 84);
    cancel.position.set(w / 2, 560);
    m.panel.addChild(tag, title, pr, note, buy, cancel);
  });
}

/** Fake 5-second rewarded video. The X closes it at any time – but only a finished video pays. */
export function testRewardedVideo(parent: Container, l: Layout | undefined): Promise<boolean> {
  return new Promise((resolve) => {
    const left = l?.left ?? 0;
    const width = (l?.right ?? SCREEN.width) - left;
    const height = l?.bottom ?? SCREEN.height;
    const root = new Container();
    const bg = new Graphics().rect(left, 0, width, height).fill(COLORS.cocoa);
    bg.eventMode = 'static';
    const tag = label('TEST VIDEO', 30, { weight: FONT.weight.heading, color: 'cream' });
    tag.position.set(SCREEN.width / 2, (l?.safeTop ?? 92) + 40);
    const dog = spriteFit('dog_honey_happy', 420);
    dog.anchor.set(0.5);
    dog.position.set(SCREEN.width / 2, height / 2 - 60);
    gsap.to(dog, { rotation: 0.08, duration: 0.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    const text = label('A real ad would play here.', FONT.size.body, { weight: FONT.weight.body, color: 'cream' });
    text.position.set(SCREEN.width / 2, height / 2 + 220);
    const barW = 600;
    const track = new Graphics().roundRect(-barW / 2, -14, barW, 28, 14).fill(COLORS.wood).stroke({ width: SHAPE.outline, color: COLORS.cream });
    const fill = new Graphics().roundRect(-barW / 2, -14, barW, 28, 14).fill(COLORS.butter);
    fill.scale.x = 0;
    fill.x = -barW / 2;
    fill.pivot.x = -barW / 2;
    const bar = new Container();
    bar.addChild(track, fill);
    bar.position.set(SCREEN.width / 2, height / 2 + 300);
    let finished = false;
    const finish = (ok: boolean) => {
      gsap.killTweensOf([dog, fill.scale]);
      root.destroy({ children: true });
      resolve(ok);
    };
    const close = iconButton('close', () => finish(finished), undefined, 80);
    close.position.set(SCREEN.width - 90, (l?.safeTop ?? 92) + 40);
    root.addChild(bg, tag, dog, text, bar, close);
    parent.addChild(root);
    gsap.to(fill.scale, {
      x: 1,
      duration: 5,
      ease: 'none',
      onComplete: () => {
        finished = true;
        text.text = 'Reward earned! Tap ✕ to continue.';
      },
    });
  });
}
